import React, { useEffect, useRef } from 'react'
import { Col, Divider, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MAPBOX_ACCESS_TOKEN } from '@slerp/client'
import { QUERY_ORDER_LOCATIONS } from './ReportsQueries'
import { useQuery } from '@apollo/client'
import Loading from './../Utils/Loading'

// Set once at module load — avoids repeated writes to the global singleton.
if (MAPBOX_ACCESS_TOKEN) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN
}

const DEFAULT_CENTER: [number, number] = [-0.1285204, 51.5137137]
const DEFAULT_ZOOM = 11

interface OrderMapProps {
  merchantId: string
  storeId: string | null
  period: number
  periodBoundaries: string[][]
}

interface HeatPoint {
  lat: number
  lng: number
}

const toGeoJSON = (points: HeatPoint[]): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: points.map(({ lat, lng }) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {}
  }))
})

const OrderMap = (props: OrderMapProps) => {
  const { storeId, merchantId, period, periodBoundaries } = props
  const [startDate] = periodBoundaries[period]
  const { data, loading } = useQuery(QUERY_ORDER_LOCATIONS, {
    variables: { merchantId, storeId, startDate }
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const mapReadyRef = useRef(false)
  const pointsRef = useRef<HeatPoint[]>([])

  useEffect(() => {
    if (!data) return
    const points = data.view_order_coords
      .filter((order: any) => order.coords?.lat && order.coords?.lng)
      .map((order: any) => ({
        lat: parseFloat(order.coords.lat),
        lng: parseFloat(order.coords.lng)
      }))
    pointsRef.current = points
    const map = mapRef.current
    if (!map || !mapReadyRef.current) return
    const source = map.getSource('orders') as mapboxgl.GeoJSONSource | undefined
    source?.setData(toGeoJSON(points))
  }, [data])

  useEffect(() => {
    if (!containerRef.current) return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      dragRotate: false,
      touchPitch: false
    })

    map.on('load', () => {
      map.addSource('orders', {
        type: 'geojson',
        data: toGeoJSON(pointsRef.current)
      })

      map.addLayer({
        id: 'orders-heat',
        type: 'heatmap',
        source: 'orders',
        paint: {
          'heatmap-radius': 30,
          'heatmap-opacity': 1,
          'heatmap-intensity': 1,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(0,0,255,0)',
            0.2,
            'royalblue',
            0.4,
            'cyan',
            0.6,
            'lime',
            0.8,
            'yellow',
            1,
            'red'
          ]
        }
      })

      mapReadyRef.current = true
      map.resize()
    })

    mapRef.current = map

    return () => {
      mapReadyRef.current = false
      map.remove()
      mapRef.current = null
    }
  }, [])

  return (
    <Col
      style={{ height: '954px' }}
      className='container-style  _pl-0 _pr-0'
      span={24}
    >
      <Col span={24} className='_pt-12 _pr-16 _pb-12 _pl-16'>
        <span className='_mt-0'>Orders Heat Map</span>
        <Tooltip
          placement='right'
          title={
            <span>
              A visual overview of{' '}
              <a
                href='https://support.slerp.com/en_gb/heat-map-overview-HkQZPemlY'
                target='_blank'
                rel='noopener noreferrer'
              >
                delivery area hotspots
              </a>
              . We suggest targeting these areas with social ads.
              <br />
              <br />
              Talk to a member of our team about our{' '}
              <a
                href='https://www.slerp.com/digital-marketing/'
                target='_blank'
                rel='noopener noreferrer'
              >
                digital marketing
              </a>{' '}
              service.
            </span>
          }
          className='_ml-8'
        >
          <QuestionCircleOutlined />
        </Tooltip>
      </Col>
      <Divider className='_mt-0 _mb-0' />
      <div
        style={{ position: 'relative', height: '92.5%', overflow: 'hidden' }}
      >
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Loading />
          </div>
        )}
        {!MAPBOX_ACCESS_TOKEN ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#888'
            }}
          >
            Map unavailable — missing configuration.
          </div>
        ) : (
          <div
            ref={containerRef}
            style={{
              position: 'absolute',
              inset: '24px 16px 16px 16px'
            }}
          />
        )}
      </div>
    </Col>
  )
}

export default OrderMap
